import React, { useContext, useMemo } from "react";
import { useEffect, useState, useRef } from "react";
import { format, startOfDay, endOfDay, subDays } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import EntityConstants from "../../configs/EntityConstants";
import { GET, POST } from "../../service/CRUDService";
import toast from "react-hot-toast";
import CfLoader from "../../common/CfLoader/CfLoader";
import IssuedCredentialLongCard from "../../components/Cards/IssuedCredentialLongCard/IssuedCredentialLongCard";
import { useTranslation } from "react-i18next";
import Dropdown from "../../common/Dropdown/Dropdown";
import AuthContext from "../../store/auth-context";
import { CalendarDays } from "lucide-react";
import SmallCFLoader from "../../common/SmallCFLoader/SmallCFLoader";
import { DownloadFileFromUrl } from "../../service/DownloadFileFromUrl";
import ExportIcon from "../../resources/icons/document-export.svg";

const AllTransactions = () => {
  const authContext = useContext(AuthContext);
  const { t } = useTranslation();

  const [count, setCount] = useState(0);
  const [limit, setLimit] = useState(6);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [previous, setPrevious] = useState(null);
  const [next, setNext] = useState(null);
  const [issuer, setIssuer] = useState("");
  const [respContentList, setRespContentList] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearched, setIsSearched] = useState(false);
  const [dateRange, setDateRange] = useState("last6months");
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const exportIntervalRef = useRef(null);
  const EXPORT_POLL_INTERVAL = 3000;

  const formatDateForAPI = (date) => {
    if (!date) return "";
    return new Date(date).toISOString();
  };

  const getDateRangeForAPI = () => {
    const now = new Date();
    let startDate, endDate;

    switch (dateRange) {
      case "recent":
        startDate = subDays(startOfDay(now), 6);
        endDate = endOfDay(now);
        break;
      case "last30days":
        startDate = subDays(startOfDay(now), 29);
        endDate = endOfDay(now);
        break;
      case "last3months":
        startDate = subDays(startOfDay(now), 89);
        endDate = endOfDay(now);
        break;
      case "last6months":
        startDate = subDays(startOfDay(now), 179);
        endDate = endOfDay(now);
        break;
      case "all":
        return { all_transactions: true };
      case "custom":
        if (customStartDate && customEndDate) {
          startDate = startOfDay(customStartDate);
          endDate = endOfDay(customEndDate);
        } else {
          startDate = subDays(startOfDay(now), 6);
          endDate = endOfDay(now);
        }
        break;
      default:
        startDate = subDays(startOfDay(now), 6);
        endDate = endOfDay(now);
    }

    return {
      from: formatDateForAPI(startDate),
      to: formatDateForAPI(endDate),
    };
  };

  const handleExport = async () => {
    if (isExporting) return;

    setIsExporting(true);

    const dateRangeParams = getDateRangeForAPI();

    try {
      const payload = {
        ...(dateRangeParams.all_transactions && { all_transactions: true }),
        ...(dateRangeParams.from && { from: dateRangeParams.from }),
        ...(dateRangeParams.to && { to: dateRangeParams.to }),
        ...(status && { verification_approval_status: status }),
        ...(search && { search: search }),
      };

      const response = await POST(
        EntityConstants.POST_DOWNLOAD_TRANSACTIONS,
        payload,
      );

      if (!response.ok) {
        throw new Error("Failed to start export");
      }

      const startData = await response.json();
      const { export_id } = startData;

      if (!export_id) {
        throw new Error("Export ID not received");
      }

      let pollCount = 0;
      const MAX_POLLS = 20;

      const pollStatus = async () => {
        try {
          const statusResponse = await GET(
            `${EntityConstants.GET_DOWNLOAD_TRANSACTIONS_STATUS}/${export_id}/status/`,
          );

          if (!statusResponse.ok) {
            throw new Error("Failed to fetch export status");
          }

          const statusData = await statusResponse.json();
          pollCount += 1;

          if (pollCount >= MAX_POLLS) {
            clearInterval(exportIntervalRef.current);
            exportIntervalRef.current = null;
            setIsExporting(false);
            toast.error(t("transaction:export_timeout"));
            return;
          }

          if (statusData.status === "COMPLETED") {
            clearInterval(exportIntervalRef.current);
            exportIntervalRef.current = null;
            setIsExporting(false);

            if (statusData.file_url) {
              DownloadFileFromUrl(
                statusData.file_url,
                `transactions_${export_id}.xlsx`,
              );
              toast.success(t("transaction:export_completed"));
            }
          } else if (statusData.status === "FAILED") {
            clearInterval(exportIntervalRef.current);
            exportIntervalRef.current = null;
            setIsExporting(false);
            toast.error(t("transaction:export_failed"));
          }
        } catch (error) {
          clearInterval(exportIntervalRef.current);
          exportIntervalRef.current = null;
          setIsExporting(false);
          toast.error(t("transaction:export_failed"));
        }
      };

      exportIntervalRef.current = setInterval(pollStatus, EXPORT_POLL_INTERVAL);
    } catch (error) {
      setIsExporting(false);
      toast.error(error.message || t("transaction:export_failed"));
    }
  };

  useEffect(() => {
    return () => {
      if (exportIntervalRef.current) {
        clearInterval(exportIntervalRef.current);
      }
    };
  }, []);

  const updateStatus = (value) => {
    setStatus(value);
  };

  const options2 = [
    { name: t("transaction:status_label"), value: "" },
    { name: t("transaction:status_approved"), value: "Approved" },
    { name: t("transaction:status_rejected"), value: "Rejected" },
    {
      name: t("transaction:status_approval_pending"),
      value: "Approval Pending",
    },
    { name: t("transaction:status_payment_pending"), value: "Payment Pending" },
  ];

  const GET_ALL_TRANSACTIONS_LIST = (limit, offset, status, issuer) => {
    setIsSearching(true);
    const temp_array = respContentList;
    setRespContentList([]);

    const dateRangeParams = getDateRangeForAPI();

    const body = {
      ...dateRangeParams,
      issuer_id: issuer,
      status: status,
      search: null,
    };

    if (body.all_transactions) {
      delete body.from;
      delete body.to;
    }

    POST(
      `${EntityConstants.GET_TRANSACTIONS_DETAILS}?limit=${limit}&offset=${offset}`,
      body,
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          toast.error(data.error);
          setRespContentList(temp_array);
        } else {
          setCount(data.count);
          authContext.IssuedOffsetHandler(offset);
          setRespContentList(data.results);
          setPrevious(data.previous);
          setNext(data.next);
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsSearching(false);
        setIsSearched(true);
      });
  };

  const SEARCH = (limit, offset, status, issuer, search) => {
    setIsSearching(true);

    const dateRangeParams = getDateRangeForAPI();

    const body = {
      ...dateRangeParams,
      issuer_id: issuer,
      status: status,
      search: search,
    };

    if (body.all_transactions) {
      delete body.from;
      delete body.to;
    }

    POST(
      `${EntityConstants.GET_TRANSACTIONS_DETAILS}?limit=${limit}&offset=${offset}`,
      body,
    )
      .then((response) => response.json())
      .then((data) => {
        setRespContentList(data.results);
        setCount(data.count);
        setPrevious(data.previous);
        setNext(data.next);
        setIsSearched(true);
        setIsSearching(false);
      })
      .catch((error) => {
        console.error("Error fetching search results:", error);
        setIsSearching(false);
        setIsSearched(false);
      });
  };

  useEffect(() => {
    if (search) {
      SEARCH(limit, authContext.issuedOffset, status, issuer, search);
    } else {
      GET_ALL_TRANSACTIONS_LIST(
        limit,
        authContext.issuedOffset,
        status,
        issuer,
      );
    }
  }, [
    search,
    dateRange,
    customStartDate,
    customEndDate,
    status,
    limit,
    issuer,
    authContext.issuedOffset,
  ]);

  useEffect(() => {
    if (authContext.issuedOffset !== null) {
      localStorage.setItem("transactionOffset", authContext.issuedOffset);
    }
  }, [localStorage.setItem("transactionOffset", authContext.issuedOffset)]);

  const DateFilterDropdown = () => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [tempStartDate, setTempStartDate] = useState(customStartDate);
    const [tempEndDate, setTempEndDate] = useState(customEndDate);

    const today = useMemo(() => new Date(), []);

    const minStartDate = useMemo(() => {
      const minDate = new Date(today);
      minDate.setDate(minDate.getDate() - 180);
      return minDate;
    }, [today]);

    const maxEndDate = useMemo(() => {
      if (!tempStartDate) return today;

      const maxFromStartDate = new Date(tempStartDate);
      maxFromStartDate.setDate(maxFromStartDate.getDate() + 180);

      return maxFromStartDate < today ? maxFromStartDate : today;
    }, [tempStartDate, today]);

    const minEndDate = useMemo(() => {
      return tempStartDate || minStartDate;
    }, [tempStartDate, minStartDate]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          setIsOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const presetOptions = [
      { label: t("transaction:filter_recent"), value: "recent" },
      { label: t("transaction:filter_last_30_days"), value: "last30days" },
      { label: t("transaction:filter_last_3_months"), value: "last3months" },
      { label: t("transaction:filter_last_6_months"), value: "last6months" },
      { label: t("transaction:filter_all"), value: "all" },
    ];

    const handlePresetSelect = (preset) => {
      setDateRange(preset);
      setCustomStartDate(null);
      setCustomEndDate(null);
      setIsOpen(false);
    };

    const handleApplyCustomDate = () => {
      if (tempStartDate && tempEndDate && tempEndDate >= tempStartDate) {
        setCustomStartDate(tempStartDate);
        setCustomEndDate(tempEndDate);
        setDateRange("custom");
        setIsOpen(false);
      }
    };

    const getDisplayText = () => {
      if (dateRange === "custom" && customStartDate && customEndDate) {
        return `${format(customStartDate, "MMM d, yyyy")} - ${format(
          customEndDate,
          "MMM d, yyyy",
        )}`;
      }
      return (
        presetOptions.find((opt) => opt.value === dateRange)?.label || "Recent"
      );
    };

    return (
      <div className="relative inline-block text-left" ref={dropdownRef}>
        <button
          id="all-transactions-date-filter-trigger-button"
          type="button"
          className="inline-flex justify-between items-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
          onClick={() => setIsOpen(!isOpen)}
        >
          {getDisplayText()}
          <svg
            className="-mr-1 ml-2 h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        {isOpen && (
          <div className="origin-top-left absolute left-0 mt-2 w-[23rem] rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
            <div className="" role="menu" aria-orientation="vertical">
              {presetOptions.map((option) => (
                <button
                  key={option.value}
                  id={`all-transactions-date-filter-${option.value}-button`}
                  className={`${
                    dateRange === option.value
                      ? "bg-gray-100 text-gray-900"
                      : "text-gray-700"
                  } block w-full text-left px-4 py-2 text-sm hover:bg-gray-100`}
                  onClick={() => handlePresetSelect(option.value)}
                >
                  {option.label}
                </button>
              ))}

              <hr className=" border-gray-200" />

              <div className="px-4 py-2 text-sm text-gray-500 font-medium">
                {t("transaction:custom_period_label")}
              </div>

              <div className="px-4 py-2 space-y-2">
                <div className="flex items-center space-x-3">
                  <div className="relative w-[10rem]">
                    <DatePicker
                      id="all-transactions-start-date-input"
                      selected={tempStartDate}
                      onChange={(date) => {
                        setTempStartDate(date);

                        if (!date) {
                          setTempEndDate(null);
                          return;
                        }

                        if (tempEndDate && tempEndDate < date) {
                          setTempEndDate(null);
                          return;
                        }

                        const maxFromStart = new Date(date);
                        maxFromStart.setDate(maxFromStart.getDate() + 180);
                        const upperBound =
                          maxFromStart < today ? maxFromStart : today;

                        if (tempEndDate && tempEndDate > upperBound) {
                          setTempEndDate(null);
                        }
                      }}
                      selectsStart
                      startDate={tempStartDate}
                      endDate={tempEndDate}
                      dateFormat={"dd/MM/yyyy"}
                      className="w-full border border-gray-300 rounded pl-12 pr-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      placeholderText={t("transaction:start_date")}
                      minDate={minStartDate}
                      maxDate={today}
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      yearDropdownItemNumber={15}
                      scrollableYearDropdown
                    />
                    <CalendarDays className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500 pointer-events-none" />
                  </div>

                  <div className="relative w-[10rem]">
                    <DatePicker
                      id="all-transactions-end-date-input"
                      selected={tempEndDate}
                      onChange={(date) => setTempEndDate(date)}
                      selectsEnd
                      startDate={tempStartDate}
                      endDate={tempEndDate}
                      dateFormat={"dd/MM/yyyy"}
                      minDate={minEndDate}
                      maxDate={maxEndDate}
                      className="w-full border border-gray-300 rounded pl-12 pr-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      placeholderText={t("transaction:end_date")}
                      disabled={!tempStartDate}
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      yearDropdownItemNumber={15}
                      scrollableYearDropdown
                    />
                    <CalendarDays className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500 pointer-events-none" />
                  </div>
                </div>
                {!tempStartDate ? (
                  <div className="text-xs text-gray-500">
                    {t("transaction:select_start_date_between")}{" "}
                    {format(minStartDate, "MMM d, yyyy")}{" "}
                    {t("transaction:and_conjunction")}{" "}
                    {format(today, "MMM d, yyyy")}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">
                    {t("transaction:end_date_must_be_between")}{" "}
                    {format(tempStartDate, "MMM d, yyyy")}{" "}
                    {t("transaction:and_conjunction")}{" "}
                    {format(maxEndDate, "MMM d, yyyy")}
                  </div>
                )}

                <button
                  id="all-transactions-date-filter-apply-button"
                  onClick={handleApplyCustomDate}
                  disabled={
                    !tempStartDate ||
                    !tempEndDate ||
                    tempEndDate < tempStartDate
                  }
                  className="w-full bg-cfPrimary text-white font-medium py-2 px-4 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed mt-3"
                >
                  {t("transaction:apply")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col items-center w-full pb-3 pt-20 bg-cfSecondary min-h-screen ">
      <div className="w-full flex flex-col justify-center">
        <div
          className={`w-full bg-cfSecondary flex justify-center py-[0.8rem] px-8`}
        >
          <div
            className={`flex flex-col lg-xl:flex-row items-start lg-xl:items-center justify-between w-full`}
          >
            <span className="flex items-center gap-2 w-full">
              <DateFilterDropdown />
              <Dropdown
                id="all-transactions-status-select"
                optionIdPrefix="all-transactions-status-option"
                options={options2}
                selected={
                  options2.find((option) => option.value === status) ||
                  options2[0]
                }
                onSelect={updateStatus}
                buttonClass="border"
                menuClass="z-20"
              />
            </span>

            <div className="flex justify-end items-end gap-4 w-full">
              <button
                id="all-transactions-export-button"
                onClick={handleExport}
                disabled={
                  isExporting ||
                  respContentList.length === 0 ||
                  dateRange === "all"
                }
                className={`flex items-center justify-center gap-2 px-6 py-2 rounded-[8px] text-white font-medium text-md ${
                  isExporting ||
                  respContentList.length === 0 ||
                  dateRange === "all"
                    ? "bg-indigo-300 cursor-not-allowed"
                    : "bg-cfPrimary hover:bg-indigo-700"
                }`}
              >
                {isExporting ? (
                  <SmallCFLoader text={t("transaction:processing")} />
                ) : (
                  <>
                    {t("transaction:export")}
                    <img src={ExportIcon} className="w-5 h-5" alt="Export" />
                  </>
                )}
              </button>

              {/* Search */}
              <form
                onSubmit={(e) => e.preventDefault()}
                className="bg-cfSecondary p-2 rounded-lg w-full lg-xl:w-[25rem] self-center  md:self-end md:m-0 border xxs:mt-2"
              >
                <input
                  type="text"
                  name="search"
                  id="transactions-search-input"
                  onChange={(e) => {
                    authContext.IssuedOffsetHandler(0); //! This will reset the pagination when search query changes
                    setSearch(e.target.value.toLowerCase());
                  }}
                  placeholder={t(
                    "transaction:search_placeholder_issuer_transaction",
                  )}
                  className="outline-none border-none p-0 placeholder:text-sm w-full"
                  style={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}
                />
              </form>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <div className="flex flex-col justify-center gap-2 min-w-[90rem] md:w-full px-8">
            {isSearching ? (
              <CfLoader />
            ) : isSearched &&
              respContentList &&
              respContentList.length === 0 ? (
              <div className="flex justify-center items-center w-full py-3 bg-cfSecondary text-gray-600 font-semibold text-lg">
                {t("transaction:no_results_found")}
              </div>
            ) : (
              respContentList &&
              respContentList.map(
                ({
                  transaction_id,
                  issuer,
                  verified_at,
                  updated_at,
                  verification_status,
                  svg_url,
                  pdf_url,
                  certificate_name,
                  document_type,
                  payment_status,
                  verification_cost_overall,
                  order_id,
                  uploaded_document,
                  mode_of_verification,
                  certificate_id,
                  payment_id,
                  programme_name,
                  programme_code,
                  recipient_name,
                  recipient_email,
                  issuer_logo,
                  issuer_organisation,
                  verification_cost,
                  comments,
                  payment_signature,
                  org_code,
                  razorpay_client_id,
                }) => (
                  <IssuedCredentialLongCard
                    transaction_id={transaction_id}
                    name={certificate_name}
                    credential_template_id={transaction_id}
                    issuer={issuer}
                    svg_url={svg_url}
                    pdf_url={pdf_url}
                    hasAnyUrls={respContentList.some(
                      (item) => item.svg_url || item.pdf_url,
                    )}
                    issuer_credential_template_id={""}
                    transaction_status={verification_status}
                    completed_at={verified_at}
                    updated_at={updated_at}
                    lastEdited={""}
                    thumbnail_image={""}
                    issued_by={""}
                    userProfileImg={""}
                    receivers={""}
                    document_type={document_type}
                    payment_status={payment_status}
                    verification_cost_overall={verification_cost_overall}
                    order_id={order_id}
                    uploaded_document={uploaded_document}
                    mode_of_verification={mode_of_verification}
                    certificate_id={certificate_id}
                    verification_status={verification_status}
                    payment_id={payment_id}
                    programme_name={programme_name}
                    programme_code={programme_code}
                    recipient_name={recipient_name}
                    recipient_email={recipient_email}
                    issuer_logo={issuer_logo}
                    issuer_organisation={issuer_organisation}
                    verification_cost={verification_cost}
                    comments={comments}
                    payment_signature={payment_signature}
                    org_code={org_code}
                    razorpay_client_id={razorpay_client_id}
                  />
                ),
              )
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-6 px-8 w-full">
        <button
          id="all-transactions-pagination-previous-button"
          onClick={() =>
            authContext.IssuedOffsetHandler(authContext.issuedOffset - limit)
          }
          className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
          disabled={previous === null}
        >
          <span className="flex items-center justify-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="w-5 h-5 rtl:-scale-x-100"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
              />
            </svg>

            <span>{t("transaction:previous")}</span>
          </span>
        </button>

        <button
          id="all-transactions-pagination-next-button"
          onClick={() =>
            authContext.IssuedOffsetHandler(authContext.issuedOffset + limit)
          }
          className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
          disabled={next === null}
        >
          <span className="flex items-center justify-center gap-2">
            <span>{t("transaction:next")}</span>

            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="w-5 h-5 rtl:-scale-x-100"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
              />
            </svg>
          </span>
        </button>
      </div>
    </div>
  );
};

export default AllTransactions;
